import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography, Button } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DashboardHome from './pages/DashboardHome';
import Customers from './pages/Customers';
import Clients from './pages/Clients';
import Services from './pages/Services';
import ServiceRequests from './pages/ServiceRequests';
import CategoryRequests from './pages/CategoryRequests';
import Bookings from './pages/Bookings';
import PaymentLifecycle from './pages/PaymentLifecycle';
import ClientApprovals from './pages/ClientApprovals';
import CommunicationTemplates from './pages/CommunicationTemplates';
import NotificationTemplates from './pages/NotificationTemplates';
import PlatformSettings from './pages/PlatformSettings';
import Categories from './pages/Categories';
import Coupons from './pages/Coupons';
import AdminReassignBookings from './pages/AdminReassignBookings';
import AdminAssignLeads from './pages/AdminAssignLeads';
import Refunds from './pages/Refunds';
import UserDashboard from './pages/UserDashboard';
import UserBookings from './pages/UserBookings';
import UserServices from './pages/UserServices';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminPrivacyPolicy from './pages/AdminPrivacyPolicyClean';
import TermsPage from './pages/TermsPage';
import AdminTermsEditor from './pages/AdminTermsEditor';
import PoliciesAdmin from './pages/PoliciesAdmin';
import SupportDesk from './pages/HelpDesk';
import Marketing from './pages/Marketing';
import TeamAccessControl from './pages/TeamAccessControl';
import { DASHBOARD_PERMISSIONS } from './constants/dashboardAccess';

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, isAdmin, isApproved, approvalStatus, userRole, loading, logout, refreshAccessControl } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Check if client is pending approval
  if (!isApproved && userRole?.client && approvalStatus === 'pending') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
          textAlign: 'center',
          p: 3
        }}
      >
        <Box sx={{ fontSize: 60 }}>⏳</Box>
        <Typography variant="h4" gutterBottom>
          Account Under Review
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          Your application is being reviewed. You'll be able to login once approved within 24 hours.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Thank you for your patience!
        </Typography>
      </Box>
    );
  }

  // Check if client was rejected
  if (!isApproved && userRole?.client && approvalStatus === 'rejected') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
          textAlign: 'center',
          p: 3
        }}
      >
        <Box sx={{ fontSize: 60 }}>❌</Box>
        <Typography variant="h4" gutterBottom>
          Application Not Approved
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          Unfortunately, your application was not approved at this time. Please contact support for more information.
        </Typography>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
          textAlign: 'center',
          p: 3
        }}
      >
        <Box sx={{ fontSize: 50 }}>🔒</Box>
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
          You are signed in as <strong>{currentUser?.email || currentUser?.uid}</strong>, but this account does not have admin privileges.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
          UID: {currentUser?.uid}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="outlined" color="primary" onClick={() => logout()}>
            Logout / Switch Account
          </Button>
          <Button variant="contained" color="primary" onClick={() => refreshAccessControl()}>
            Re-check Permissions
          </Button>
        </Box>
      </Box>
    );
  }

  return children;
};

const RestrictedAccessNotice = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      minHeight: 320,
      flexDirection: 'column',
      textAlign: 'center',
      gap: 1.5,
      p: 3,
    }}
  >
    <Typography variant="h5">No Dashboard Tasks Assigned</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
      Your account is admin-approved, but no dashboard permissions are currently assigned. Contact the main admin to assign team access.
    </Typography>
  </Box>
);

const PermissionRoute = ({ permission, children }) => {
  const { loading, hasPermission, getFirstAllowedDashboardPath } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission(permission)) {
    const fallbackPath = getFirstAllowedDashboardPath();
    if (
      fallbackPath &&
      fallbackPath !== '/login' &&
      fallbackPath !== location.pathname
    ) {
      return <Navigate to={fallbackPath} replace />;
    }

    return <RestrictedAccessNotice />;
  }

  return children;
};

const DashboardIndexRoute = () => {
  const { hasPermission, getFirstAllowedDashboardPath } = useAuth();

  if (hasPermission(DASHBOARD_PERMISSIONS.OVERVIEW)) {
    return <DashboardHome />;
  }

  const fallbackPath = getFirstAllowedDashboardPath();
  if (fallbackPath && fallbackPath !== '/dashboard' && fallbackPath !== '/login') {
    return <Navigate to={fallbackPath} replace />;
  }

  return <RestrictedAccessNotice />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-provider" element={<TermsPage docId="termsProvider" defaultTitle="Provider Terms and Conditions" />} />
            <Route path="/terms-customer" element={<TermsPage docId="termsCustomer" defaultTitle="Customer Terms and Conditions" />} />
            <Route path="/login" element={<LoginRoute />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardIndexRoute />} />
              <Route
                path="customers"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.CUSTOMERS}>
                    <Customers />
                  </PermissionRoute>
                }
              />
              <Route
                path="clients"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.PROVIDERS}>
                    <Clients />
                  </PermissionRoute>
                }
              />
              <Route
                path="client-approvals"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION}>
                    <ClientApprovals />
                  </PermissionRoute>
                }
              />
              <Route
                path="document-uploads"
                element={<Navigate to="/dashboard/clients" replace />}
              />
              <Route
                path="services"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.SERVICES}>
                    <Services />
                  </PermissionRoute>
                }
              />
              <Route
                path="service-requests"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.SERVICES}>
                    <ServiceRequests />
                  </PermissionRoute>
                }
              />
              <Route
                path="categories"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.CATEGORIES}>
                    <Categories />
                  </PermissionRoute>
                }
              />
              <Route
                path="category-requests"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.CATEGORIES}>
                    <CategoryRequests />
                  </PermissionRoute>
                }
              />
              <Route
                path="coupons"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.COUPONS}>
                    <Coupons />
                  </PermissionRoute>
                }
              />
              <Route
                path="bookings"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.BOOKINGS}>
                    <Bookings />
                  </PermissionRoute>
                }
              />
              <Route
                path="payment-lifecycle"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.BOOKINGS}>
                    <PaymentLifecycle />
                  </PermissionRoute>
                }
              />
              <Route
                path="reassign-bookings"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.OPEN_POOL}>
                    <AdminReassignBookings />
                  </PermissionRoute>
                }
              />
              <Route
                path="reassign-leads"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.OPEN_POOL}>
                    <AdminAssignLeads />
                  </PermissionRoute>
                }
              />
              <Route
                path="refunds"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.REFUNDS}>
                    <Refunds />
                  </PermissionRoute>
                }
              />
              <Route
                path="marketing"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.MARKETING}>
                    <Marketing />
                  </PermissionRoute>
                }
              />
              <Route
                path="communication"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES}>
                    <CommunicationTemplates />
                  </PermissionRoute>
                }
              />
              <Route
                path="notifications"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.SMS_TEMPLATES}>
                    <NotificationTemplates />
                  </PermissionRoute>
                }
              />
              <Route
                path="sms"
                element={<Navigate to="/dashboard/notifications" replace />}
              />
              <Route
                path="settings"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS}>
                    <PlatformSettings />
                  </PermissionRoute>
                }
              />
              <Route
                path="support"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.HELPDESK}>
                    <SupportDesk />
                  </PermissionRoute>
                }
              />
              <Route
                path="helpdesk"
                element={<Navigate to="/dashboard/support" replace />}
              />
              <Route
                path="policies"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.POLICIES}>
                    <PoliciesAdmin />
                  </PermissionRoute>
                }
              />
              <Route
                path="privacy-policy"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.POLICIES}>
                    <AdminPrivacyPolicy />
                  </PermissionRoute>
                }
              />
              <Route
                path="terms-provider"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.POLICIES}>
                    <AdminTermsEditor docId="termsProvider" defaultTitle="Provider Terms and Conditions" />
                  </PermissionRoute>
                }
              />
              <Route
                path="terms-customer"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.POLICIES}>
                    <AdminTermsEditor docId="termsCustomer" defaultTitle="Customer Terms and Conditions" />
                  </PermissionRoute>
                }
              />
              <Route
                path="team-access"
                element={
                  <PermissionRoute permission={DASHBOARD_PERMISSIONS.TEAM_ACCESS}>
                    <TeamAccessControl />
                  </PermissionRoute>
                }
              />
            </Route>

            {/* User Routes */}
            <Route path="/user" element={<UserDashboard />}>
              <Route index element={<Navigate to="/user/bookings" />} />
              <Route path="bookings" element={<UserBookings />} />
              <Route path="services" element={<UserServices />} />
            </Route>
            
            {/* Default Route */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Login Route Component (redirects if already logged in)
const LoginRoute = () => {
  const { currentUser, isAdmin, loading, getFirstAllowedDashboardPath } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (currentUser && isAdmin) {
    return <Navigate to={getFirstAllowedDashboardPath()} />;
  }

  return <Login />;
};

export default App;
